import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Chart from 'react-apexcharts';
import { Card, Metric, Text } from '@tremor/react';

const apiUrlPedidos = `${process.env.REACT_APP_API_URL}/seleccion`;
const apiUrlAddresses = `${process.env.REACT_APP_API_URL}/addres`;

const CardGripMapSedes = () => {
    const [pedidos, setPedidos] = useState([]);
    const [sedes, setSedes] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            try {
                // 1. Cargar Sedes para tener los nombres bonitos
                const resSedes = await axios.get(apiUrlAddresses);
                const sedesMap = {};
                resSedes.data.forEach(s => {
                    sedesMap[s.id_direccion] = s.nombre_sede || `${s.direccion} - ${s.localidad}`;
                });
                setSedes(sedesMap);

                // 2. Cargar Pedidos (todos, para el ranking)
                const resPedidos = await axios.get(apiUrlPedidos);
                setPedidos(resPedidos.data);
            } catch (error) {
                console.error('Error al cargar datos de sedes:', error);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, []);

    // Procesar datos para el ranking
    const statsSedes = {};
    pedidos.forEach(p => {
        const idSede = p.id_sede || 'Sin Sede';
        const nombreSede = sedes[idSede] || p.ubicacion || 'Desconocida';

        if (!statsSedes[nombreSede]) {
            statsSedes[nombreSede] = { total: 0, Menu: 0, Veggie: 0, Pechuga: 0 };
        }

        statsSedes[nombreSede].total += 1;
        const tipoAgrupado = (p.tipo === 'General' || p.tipo === 'Menu') ? 'Menu' : p.tipo;
        if (statsSedes[nombreSede].hasOwnProperty(tipoAgrupado)) {
            statsSedes[nombreSede][tipoAgrupado] += 1;
        }
    });

    const ranking = Object.entries(statsSedes)
        .sort((a, b) => b[1].total - a[1].total);

    const labels = ranking.map(([nombre]) => nombre);
    const dataTotals = ranking.map(([_, data]) => data.total);
    const dataMenu = ranking.map(([_, data]) => data.Menu);
    const dataVeggie = ranking.map(([_, data]) => data.Veggie);
    const dataPechuga = ranking.map(([_, data]) => data.Pechuga);

    const options = {
        chart: {
            type: 'bar',
            stacked: true,
            foreColor: '#374151',
            toolbar: { show: false },
            background: 'transparent'
        },
        plotOptions: {
            bar: {
                horizontal: true,
                barHeight: '70%',
            },
        },
        dataLabels: { enabled: true, style: { colors: ['#fff'], fontWeight: 'bold' } },
        colors: ['#3b82f6', '#10b981', '#f59e0b'],
        xaxis: {
            categories: labels,
            labels: { style: { colors: '#6b7280', fontWeight: 600 } }
        },
        yaxis: {
            labels: { style: { colors: '#6b7280', fontWeight: 600 } }
        },
        legend: {
            position: 'top',
            labels: { colors: '#374151' }
        },
        tooltip: { theme: 'light' },
        grid: {
            borderColor: '#e5e7eb',
        },
    };

    const series = [
        { name: 'Menú', data: dataMenu },
        { name: 'Veggie', data: dataVeggie },
        { name: 'Pechuga', data: dataPechuga }
    ];

    if (loading) return <div className="p-10 text-center text-gray-700 font-semibold">Cargando ranking de sedes...</div>;

    return (
        <div className="mt-8 grid grid-cols-1 gap-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {ranking.slice(0, 3).map(([nombre, data], index) => (
                    <Card key={nombre} className="bg-white border-2 shadow-xl relative overflow-hidden hover:shadow-2xl transition-all" style={{
                        borderColor: index === 0 ? '#fbbf24' : index === 1 ? '#94a3b8' : '#fb923c'
                    }}>
                        <div className={`absolute top-0 left-0 w-2 h-full`} style={{
                            backgroundColor: index === 0 ? '#fbbf24' : index === 1 ? '#94a3b8' : '#fb923c'
                        }}></div>
                        <Text className="text-xs font-bold uppercase tracking-widest mb-2" style={{
                            color: index === 0 ? '#f59e0b' : index === 1 ? '#64748b' : '#f97316'
                        }}>
                            {index === 0 ? '🥇 1er Puesto' : index === 1 ? '🥈 2do Puesto' : '🥉 3er Puesto'}
                        </Text>
                        <Metric className="text-gray-800 text-xl truncate mb-3 font-bold" title={nombre}>{nombre}</Metric>
                        <Text className="text-blue-600 text-4xl font-bold">{data.total} <span className="text-sm font-normal text-gray-500 uppercase">pedidos</span></Text>
                    </Card>
                ))}
            </div>

            <Card className="p-6 bg-white rounded-2xl border-2 border-blue-200 shadow-2xl">
                <Metric className="mb-6 text-gray-800 font-bold flex items-center gap-2">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    Comparativa General por Sede
                </Metric>
                <Chart options={options} series={series} type="bar" height={Math.max(400, ranking.length * 60)} />
            </Card>

            <div className="bg-white rounded-2xl overflow-hidden shadow-2xl border-2 border-blue-200">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gradient-to-r from-blue-500 to-cyan-600">
                        <tr>
                            <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Sede</th>
                            <th className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wider">Total</th>
                            <th className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wider">Menú</th>
                            <th className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wider">Veggie</th>
                            <th className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wider">Pechuga</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {ranking.map(([nombre, data]) => (
                            <tr key={nombre} className="hover:bg-blue-50 transition-colors">
                                <td className="px-6 py-4 text-sm font-bold text-gray-800">{nombre}</td>
                                <td className="px-6 py-4 text-center text-sm font-bold text-blue-600">{data.total}</td>
                                <td className="px-6 py-4 text-center text-sm text-gray-700 font-semibold">{data.Menu}</td>
                                <td className="px-6 py-4 text-center text-sm text-gray-700 font-semibold">{data.Veggie}</td>
                                <td className="px-6 py-4 text-center text-sm text-gray-700 font-semibold">{data.Pechuga}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CardGripMapSedes;
